import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  openingBalances,
  type OpeningBalanceListItem,
} from '@/api/openingBalances';
import DataTable, { type ColumnDef } from '@/shared/components/DataTable';

const fmt2 = (v: string | number) =>
  Number(v).toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function OpeningBalancesPage() {
  const navigate = useNavigate();
  const list = useQuery({
    queryKey: ['opening-balances'],
    queryFn: () => openingBalances.list({ limit: 200 }),
  });

  const cols = useMemo<ColumnDef<OpeningBalanceListItem>[]>(() => [
    { key: 'document_number', header: '№', width: 150, sortValue: (r) => r.document_number ?? '', cell: (r) => r.document_number },
    {
      key: 'document_date', header: 'Дата', width: 170,
      sortValue: (r) => r.document_date ?? '',
      cell: (r) => r.document_date ? new Date(r.document_date).toLocaleString('uk-UA') : '',
    },
    {
      key: 'total_debit', header: 'Σ Дебет', width: 150, align: 'right',
      sortValue: (r) => Number(r.total_debit),
      cell: (r) => fmt2(r.total_debit),
    },
    {
      key: 'total_credit', header: 'Σ Кредит', width: 150, align: 'right',
      sortValue: (r) => Number(r.total_credit),
      cell: (r) => fmt2(r.total_credit),
    },
    {
      key: 'posted', header: 'Проведено', width: 130, align: 'center',
      sortValue: (r) => r.posted ? 1 : 0,
      cell: (r) => (r.posted ? '✓ так' : 'ні'),
    },
  ], []);

  return (
    <>
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>Вступні залишки</h2>
        <button className="primary" onClick={() => navigate('/opening-balances/new')}>
          + Новий документ
        </button>
      </div>
      <p style={{ color: '#64748b', fontSize: '0.85rem' }}>
        Документ заводиться один раз при міграції на нашу систему: остатки
        з вашої попередньої системи (1С, Excel) на дату початку обліку.
        Проводки створюються через техсчет «00».
      </p>
      <DataTable<OpeningBalanceListItem>
        rows={list.data?.items ?? []}
        columns={cols}
        rowKey={(r) => r.id}
        loading={list.isLoading}
        emptyText="Немає документів."
        onRowDoubleClick={(r) => navigate(`/opening-balances/${r.id}`)}
        rowMuted={(r) => r.deletion_mark ? 0.4 : undefined}
        initialSortKey="document_date"
        initialSortDir="desc"
      />
    </>
  );
}
